<template>
  <form @submit.prevent class="cert-generation-form">
    <!-- — DADOS DO COLABORADOR — -->
    <div class="cert-form-grid">
      <!-- nome - arquivo -->
      <div class="cert-field cert-field-wide">
        <label
          for="collaborator"
          class="block mb-2 text-sm font-medium font-xolonium-force"
        >
          Funcionário
        </label>

        <div class="cert-upload-field">
          <!-- individual -->
          <input
            required
            id="collaborator"
            v-model="store.collaborator.name"
            type="text"
            placeholder="Digite ou selecione via Excel"
            class="cert-input cert-input-with-icon"
          />

          <!-- file -->
          <label
            for="excel-upload"
            class="cert-excel-upload"
            :class="[selectedFile ? 'uploaded' : '']"
          >
            <component
              :is="selectedFile ? PhCloudCheck : PhFileXls"
              class="h-6 w-6 my-3 mx-3"
            ></component>
          </label>

          <!-- Input file real (oculto) -->
          <input
            id="excel-upload"
            type="file"
            accept=".xls,.xlsx"
            class="hidden"
            @change="handleFileUpload"
          />
        </div>
      </div>

      <!-- cpf -->
      <div class="cert-field">
        <label
          for="colReceipt"
          class="block mb-2 text-sm font-medium font-xolonium-force"
          >CPF</label
        >
        <input
          required
          id="colReceipt"
          v-mask="'###.###-###-##'"
          v-model="store.collaborator.cpf"
          type="text"
          name="cpf"
          class="cert-input"
        />
      </div>

      <!-- treinamento -->
      <div class="cert-field cert-field-wide">
        <div class="md:col-span-2">
          <div class="md:col-span-2">
            <label
              class="mb-2 text-left flex text-[#2b3038] font-xolonium-force"
              for="form.type"
            >
              Treinamento <a class="text-red-600 ml-1">*</a>
            </label>
            <div class="h-14 flex rounded items-center">
              <SimpleSelect
                class="px-1 py-2 appearance-none outline-none text-gray-800 w-full bg-transparent"
                :data="trainings"
                :disableKeyboard="false"
                @selected-data="(val) => (store.training = val)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- aproveitamento -->
      <div class="cert-field">
        <label
          for="colRegistration"
          class="block mb-2 text-sm font-medium font-xolonium-force"
          >Aproveitamento (%)</label
        >
        <input
          id="colRegistration"
          v-model="store.aproveit"
          name="aproveitamento"
          type="number"
          class="cert-input"
        />
      </div>

      <!-- descrição -->
      <div class="cert-field cert-field-full">
        <label
          for="colName"
          class="block mb-2 text-sm font-medium font-xolonium-force"
          >Descrição</label
        >
        <textarea
          name="descrição"
          rows="4"
          id="colName"
          class="cert-input cert-textarea"
          type="text"
          v-model="store.descricao"
        ></textarea>
      </div>

      <!-- data e local -->
      <div class="cert-field cert-field-wide">
        <label
          for="colCategory"
          class="block mb-2 text-sm font-medium font-xolonium-force"
          >Local e data</label
        >
        <input
          id="colCategory"
          v-model="store.dateLocale"
          name="local e data"
          type="text"
          class="cert-input"
        />
      </div>

      <div class="cert-field">
        <label
          for="colRegistration"
          class="block mb-2 text-sm font-medium font-xolonium-force"
        >
          Miniaturas
        </label>
        <div class="h-14 flex rounded items-center">
          <SimpleSelect
            class="px-1 appearance-none outline-none text-gray-800 w-full bg-transparent"
            :data="companies"
            :initial-value="companies[0]"
            :disableKeyboard="false"
            @selected-data="
              (val) => {
                if (val?.value !== null) {
                  store.companies.miniature = true;
                  store.companies.m_company = val?.miniature;
                } else {
                  store.companies.miniature = false;
                  store.companies.m_company = null;
                }
              }
            "
          />
        </div>
      </div>

      <!-- assinaturas dinâmicas -->
      <div class="cert-field-full">
        <section class="dynamic-signatures-panel">
          <div class="dynamic-signatures-header">
            <div>
              <span>Assinaturas</span>
              <strong>Assinaturas dinâmicas no certificado</strong>
              <p>
                Habilite esta opção para aplicar uma ou mais assinaturas
                cadastradas na frente do certificado. A mesma seleção será usada
                em emissões individuais e em lote.
              </p>
            </div>

            <label class="signature-switch">
              <input
                v-model="useDynamicSignatures"
                type="checkbox"
                @change="handleToggleDynamicSignatures"
              />
              <span></span>
            </label>
          </div>

          <div v-if="useDynamicSignatures" class="dynamic-signatures-body">
            <div
              v-if="!availableSignatures.length"
              class="signature-empty-warning"
            >
              Nenhuma assinatura cadastrada. Acesse o menu Assinaturas e
              cadastre uma assinatura antes de gerar o certificado.
            </div>

            <template v-else>
              <div class="signature-selection-actions">
                <button type="button" @click="selectAllSignatures">
                  Selecionar todas
                </button>

                <button type="button" @click="clearSelectedSignatures">
                  Limpar seleção
                </button>

                <button type="button" @click="loadAvailableSignatures">
                  Atualizar lista
                </button>
              </div>

              <div class="signature-selection-list">
                <label
                  v-for="signature in availableSignatures"
                  :key="signature.id"
                  class="signature-selection-card"
                  :class="{
                    selected: selectedSignatureIds.includes(signature.id),
                  }"
                >
                  <input
                    v-model="selectedSignatureIds"
                    type="checkbox"
                    :value="signature.id"
                  />

                  <div class="signature-selection-preview">
                    <img
                      v-if="signature.image?.dataUrl"
                      :src="signature.image.dataUrl"
                      :alt="`Assinatura de ${signature.signer?.name}`"
                    />
                  </div>

                  <div class="signature-selection-info">
                    <strong>{{ signature.signer?.name }}</strong>

                    <span v-if="signature.signer?.role">
                      {{ signature.signer.role }}
                    </span>

                    <small>
                      {{
                        signature.placement?.page === "front"
                          ? "Frente"
                          : "Verso"
                      }}
                      • topo {{ signature.placement?.topMm }}mm • esquerda
                      {{ signature.placement?.leftMm }}mm
                    </small>
                  </div>
                </label>
              </div>
            </template>
          </div>
        </section>
      </div>

      <!-- assinatura digital -->
      <section class="digital-signature-card cert-field-full">
        <div class="digital-signature-main">
          <span>Assinatura digital</span>

          <strong>{{ currentSigningLabel }}</strong>

          <p>
            Escolha qual certificado será usado para assinar digitalmente o PDF
            final. O modo padrão continua sendo o certificado da plataforma.
          </p>
        </div>

        <div class="digital-certificate-selector">
          <label
            class="digital-certificate-option"
            :class="{ active: selectedSigningMode === 'platform' }"
          >
            <input
              v-model="selectedSigningMode"
              type="radio"
              value="platform"
              @change="selectedDigitalCertificateId = null"
            />

            <span>Plataforma</span>
            <small>X Solution / padrão atual</small>
          </label>

          <label
            class="digital-certificate-option"
            :class="{ active: selectedSigningMode === 'company' }"
          >
            <input
              v-model="selectedSigningMode"
              type="radio"
              value="company"
              @change="
                selectedDigitalCertificateId =
                  companyDigitalCertificates[0]?.id || null
              "
            />

            <span>Empresa</span>
            <small
              >{{ companyDigitalCertificates.length }} certificado(s)</small
            >
          </label>

          <label
            class="digital-certificate-option"
            :class="{ active: selectedSigningMode === 'user' }"
          >
            <input
              v-model="selectedSigningMode"
              type="radio"
              value="user"
              @change="
                selectedDigitalCertificateId =
                  userDigitalCertificates[0]?.id || null
              "
            />

            <span>Meu certificado</span>
            <small>{{ userDigitalCertificates.length }} certificado(s)</small>
          </label>
        </div>

        <div
          v-if="selectedSigningMode !== 'platform'"
          class="digital-certificate-select-row"
        >
          <label>
            {{
              selectedSigningMode === "company"
                ? "Certificado da empresa"
                : "Certificado do usuário"
            }}
          </label>

          <select v-model="selectedDigitalCertificateId">
            <option :value="null" disabled>Selecione um certificado</option>

            <option
              v-for="certificate in selectedSigningMode === 'company'
                ? companyDigitalCertificates
                : userDigitalCertificates"
              :key="certificate.id"
              :value="certificate.id"
            >
              {{ certificate.name }} • válido até
              {{ formatCertificateValidity(certificate.valid_until) }}
            </option>
          </select>

          <button type="button" @click="loadDigitalCertificates">
            Atualizar
          </button>
        </div>

        <div
          v-if="selectedDigitalCertificate"
          class="digital-certificate-preview"
        >
          <article>
            <small>Titular</small>
            <strong>
              {{
                extractCertificateName(
                  selectedDigitalCertificate.certificate_subject
                )
              }}
            </strong>
          </article>

          <article>
            <small>Validade</small>
            <strong>{{
              formatCertificateValidity(selectedDigitalCertificate.valid_until)
            }}</strong>
          </article>

          <article>
            <small>Tipo</small>
            <strong>{{ selectedDigitalCertificate.type || "A1_PFX" }}</strong>
          </article>
        </div>

        <p
          v-if="
            selectedSigningMode === 'company' &&
            !companyDigitalCertificates.length &&
            !isLoadingDigitalCertificates
          "
          class="digital-certificate-warning"
        >
          Nenhum certificado da empresa cadastrado. Acesse “Certificados
          digitais” para enviar um A1.
        </p>

        <p
          v-if="
            selectedSigningMode === 'user' &&
            !userDigitalCertificates.length &&
            !isLoadingDigitalCertificates
          "
          class="digital-certificate-warning"
        >
          Nenhum certificado do usuário cadastrado. Acesse “Certificados
          digitais” para enviar seu A1/e-CPF.
        </p>
      </section>
    </div>

    <!-- progresso da geração -->
    <section
      v-if="processState.active || processState.summary"
      class="generation-progress-panel"
      :class="processState.status"
    >
      <div class="generation-progress-header">
        <div>
          <span>
            {{
              processState.active
                ? "Processamento em andamento"
                : "Resumo da emissão"
            }}
          </span>

          <strong>{{ processTitle }}</strong>

          <p v-if="processState.active">
            {{ processState.currentLabel }}
            <template v-if="processState.currentName">
              • {{ processState.currentName }}
            </template>
          </p>

          <p v-else-if="processState.summary">
            Processo concluído em {{ processState.summary.finishedAt }}.
          </p>
        </div>

        <button
          v-if="!processState.active && processState.summary"
          type="button"
          class="generation-progress-close"
          @click="clearProcessSummary"
        >
          Limpar
        </button>
      </div>

      <div class="generation-progress-bar">
        <div :style="{ width: `${progressPercent}%` }"></div>
      </div>

      <div class="generation-progress-stats">
        <article>
          <span>Processados</span>
          <strong>{{ processState.processed }}</strong>
        </article>

        <article>
          <span>Assinados</span>
          <strong>{{ processState.signed }}</strong>
        </article>

        <article>
          <span>Publicados</span>
          <strong>{{ processState.published }}</strong>
        </article>

        <article
          :class="{
            danger: processState.publicationFailed > 0,
          }"
        >
          <span>Falhas de publicação</span>
          <strong>
            {{ processState.publicationFailed }}
          </strong>
        </article>

        <article>
          <span>No ZIP</span>
          <strong>{{ processState.zipped }}</strong>
        </article>

        <article
          :class="
            processState.failed > 0 || processState.publicationFailed > 0
              ? 'has-failures'
              : 'all-success'
          "
        >
          <!-- "!" : "✓" -->
          <span>Falhas</span>
          <strong>
            {{
              processState.failed > 0
                ? "Emissão finalizada com falhas"
                : processState.publicationFailed > 0
                ? "Emissão concluída com pendências de publicação"
                : processState.mode === "batch"
                ? "Lote concluído com sucesso"
                : "Certificado concluído com sucesso"
            }}
          </strong>
        </article>
      </div>

      <div class="generation-step-list">
        <div
          v-for="step in activeProcessSteps"
          :key="step.id"
          class="generation-step"
          :class="getStepStatus(step.id)"
        >
          <span class="generation-step-marker"></span>
          <small>{{ step.label }}</small>
        </div>
      </div>

      <div v-if="processState.logs.length" class="generation-log-list">
        <div
          v-for="log in visibleProcessLogs"
          :key="log.id"
          class="generation-log-item"
          :class="log.type"
        >
          <span>{{ log.time }}</span>
          <p>{{ log.message }}</p>
        </div>
      </div>

      <div
        v-if="!processState.active && processState.summary"
        class="generation-summary-card"
        :class="processState.failed > 0 ? 'has-failures' : 'all-success'"
      >
        <div class="generation-summary-icon">
          {{ processState.failed > 0 ? "!" : "✓" }}
        </div>

        <div>
          <strong>
            {{
              processState.failed > 0
                ? "Lote finalizado com falhas"
                : "Lote concluído com sucesso"
            }}
          </strong>

          <p>
            {{ processState.summary.total }}
            certificado(s) analisado(s),

            {{ processState.summary.processed }}
            processado(s),

            {{ processState.summary.signed }}
            assinado(s) digitalmente,

            {{ processState.summary.published }}
            publicado(s) externamente,

            {{ processState.summary.publicationFailed }}
            pendência(s) de publicação,

            {{ processState.summary.zipped }}
            adicionado(s) ao ZIP e

            {{ processState.summary.failed }}
            falha(s) na emissão.
          </p>

          <p
            v-if="
              processState.publicationFailed > 0 && processState.failed === 0
            "
            class="generation-summary-warning"
          >
            Todos os certificados foram assinados e baixados, mas alguns não
            puderam ser publicados no servidor externo. Eles poderão ser
            reenviados sem nova assinatura.
          </p>

          <p v-if="processState.failed > 0" class="generation-summary-warning">
            Revise os logs acima para identificar quais certificados falharam.
          </p>
        </div>
      </div>
    </section>

    <!-- BOTÕES DE AÇÃO -->
    <div class="cert-action-bar">
      <!-- emissão individual -->
      <button
        type="submit"
        @click="generate"
        :disabled="isExporting"
        class="cert-action-button primary"
      >
        <component :is="PhCertificate" class="w-6 h-6 mx-1 my-auto"></component>
        <span class="mx-4 my-auto">
          {{ isExporting && exportTotal === 1 ? "Gerando PDF..." : "Gerar" }}
        </span>
      </button>

      <!-- emissão em lote -->
      <button
        type="button"
        :disabled="enableBatchButton || isExporting"
        @click="generateBatch"
        class="cert-action-button secondary"
      >
        <component :is="PhUsersThree" class="w-6 h-6 mx-1 my-auto"></component>
        <span class="mx-4 my-auto">
          {{
            isExporting
              ? `Gerando ${exportProgress}/${exportTotal}`
              : "Emitir em Lote"
          }}
        </span>
      </button>
    </div>
  </form>

  <!-- stage oculto usado para renderizar um certificado por vez -->
  <div
    style="
      position: fixed;
      left: -99999px;
      top: 0;
      opacity: 0;
      pointer-events: none;
    "
    aria-hidden="true"
  >
    <div ref="pdfContainer" class="receipt-print-area">
      <ReceiptDocument
        v-if="currentExportCollaborator"
        :key="exportKey"
        :collaborator="currentExportCollaborator"
        :training="store.training"
        :aproveitamento="store.aproveit"
        :description="store.descricao"
        :location="store.dateLocale"
        :signatures="currentExportSignatures"
        :certificate="currentExportCertificate"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, computed, onMounted } from "vue";
import ReceiptDocument from "./ReceiptDocument.vue";
import SimpleSelect from "@/components/SimpleSelect.vue";
import { appStore } from "@/store";
import { trainings, description, companies } from "@/lookups/trainings";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import JSZip from "jszip";
import { listDigitalCertificates } from "@/services/digitalCertificateService";
import { saveAs } from "file-saver";
import {
  PhCertificate,
  PhCloudCheck,
  PhFileXls,
  PhUsersThree,
} from "@phosphor-icons/vue";
import { getSignatures } from "@/services/signatureStorage";
import {
  createCertificate,
  uploadCertificatePdf,
  downloadCertificatePdf,
  publishCertificate,
} from "@/services/certificateService";

const store = appStore.state;
const pdfContainer = ref(null);
const enableBatchButton = ref(true);
const selectedFile = ref(false);
const currentExportCollaborator = ref(null);
const exportKey = ref(0);
const isExporting = ref(false);
const exportProgress = ref(0);
const exportTotal = ref(0);
const t = computed(() => appStore.getters.getTrainingSelected);

const availableSignatures = ref([]);
const useDynamicSignatures = ref(false);
const selectedSignatureIds = ref([]);
const currentExportSignatures = ref([]);
const currentExportCertificate = ref(null);

const digitalCertificates = ref([]);
const isLoadingDigitalCertificates = ref(false);
const selectedSigningMode = ref("platform");
const selectedDigitalCertificateId = ref(null);

const singleProcessSteps = [
  { id: "register", label: "Registrando certificado" },
  { id: "render", label: "Gerando PDF" },
  { id: "sign", label: "Assinando digitalmente" },
  { id: "publish", label: "Publicando no servidor externo" },
  { id: "download", label: "Baixando PDF final" },
  { id: "finish", label: "Concluído" },
];

const batchProcessSteps = [
  { id: "register", label: "Registrando certificado" },
  { id: "render", label: "Gerando PDF" },
  { id: "sign", label: "Assinando digitalmente" },
  { id: "publish", label: "Publicando no servidor externo" },
  { id: "download", label: "Baixando PDF final" },
  { id: "zip", label: "Adicionando ao ZIP" },
  { id: "package", label: "Compactando lote" },
  { id: "finish", label: "Concluído" },
];

const processState = ref({
  active: false,
  mode: null,
  status: "idle",

  currentStep: null,
  currentLabel: "",
  currentName: "",
  currentIndex: 0,
  total: 0,

  processed: 0,
  signed: 0,
  published: 0,
  publicationFailed: 0,
  zipped: 0,
  failed: 0,

  startedAt: null,
  finishedAt: null,

  logs: [],
  errors: [],
  summary: null,
});

const activeProcessSteps = computed(() => {
  return processState.value.mode === "batch"
    ? batchProcessSteps
    : singleProcessSteps;
});

const currentStepIndex = computed(() => {
  return activeProcessSteps.value.findIndex((step) => {
    return step.id === processState.value.currentStep;
  });
});

const progressPercent = computed(() => {
  if (!processState.value.active && processState.value.summary) {
    return 100;
  }

  if (!processState.value.total) {
    return 0;
  }

  const stepCount = Math.max(activeProcessSteps.value.length, 1);
  const stepProgress =
    currentStepIndex.value >= 0 ? (currentStepIndex.value + 1) / stepCount : 0;

  const itemBase = Math.max(processState.value.currentIndex - 1, 0);

  return Math.min(
    100,
    Math.round(((itemBase + stepProgress) / processState.value.total) * 100)
  );
});

const visibleProcessLogs = computed(() => {
  return [...processState.value.logs].reverse();
});

const processTitle = computed(() => {
  if (!processState.value.active && processState.value.summary) {
    return processState.value.mode === "batch"
      ? "Lote concluído"
      : "Certificado concluído";
  }

  if (processState.value.mode === "batch") {
    return `Certificado ${processState.value.currentIndex || 1} de ${
      processState.value.total
    }`;
  }

  return "Emissão individual";
});

const selectedSignatures = computed(() => {
  if (!useDynamicSignatures.value) return [];

  const selectedIds = new Set(selectedSignatureIds.value);

  return availableSignatures.value.filter((signature) => {
    return selectedIds.has(signature.id);
  });
});

const companyDigitalCertificates = computed(() => {
  return digitalCertificates.value.filter((certificate) => {
    return (
      normalizeEnumValue(certificate.owner_type) === "company" &&
      !isDigitalCertificateExpired(certificate)
    );
  });
});

const userDigitalCertificates = computed(() => {
  return digitalCertificates.value.filter((certificate) => {
    return (
      normalizeEnumValue(certificate.owner_type) === "user" &&
      !isDigitalCertificateExpired(certificate)
    );
  });
});

const selectedDigitalCertificate = computed(() => {
  if (selectedSigningMode.value === "platform") {
    return null;
  }

  return digitalCertificates.value.find((certificate) => {
    return (
      Number(certificate.id) === Number(selectedDigitalCertificateId.value)
    );
  });
});

const currentSigningLabel = computed(() => {
  if (selectedSigningMode.value === "platform") {
    return "Certificado da plataforma";
  }

  if (selectedDigitalCertificate.value) {
    return selectedDigitalCertificate.value.name || "Certificado selecionado";
  }

  return selectedSigningMode.value === "company"
    ? "Certificado da empresa"
    : "Meu certificado";
});

watch(
  t,
  (newVal) => {
    const found = description.find((item) => item.name === newVal?.value);

    if (found) {
      appStore.dispatch("alterDescriptionByTraining", found?.value);
    } else {
      appStore.dispatch("alterDescriptionByTraining", null);
    }
  },
  { immediate: true }
);

function validateSelectedDigitalCertificate() {
  if (selectedSigningMode.value === "platform") {
    return true;
  }

  if (!selectedDigitalCertificateId.value) {
    alert(
      selectedSigningMode.value === "company"
        ? "Selecione um certificado digital da empresa."
        : "Selecione um certificado digital do usuário."
    );

    return false;
  }

  if (!selectedDigitalCertificate.value) {
    alert("O certificado digital selecionado não está disponível.");

    return false;
  }

  if (isDigitalCertificateExpired(selectedDigitalCertificate.value)) {
    alert("O certificado digital selecionado está vencido.");

    return false;
  }

  return true;
}

async function loadDigitalCertificates() {
  isLoadingDigitalCertificates.value = true;

  try {
    digitalCertificates.value = await listDigitalCertificates();
  } catch (error) {
    console.error("Erro ao carregar certificados digitais:", error);
    digitalCertificates.value = [];
  } finally {
    isLoadingDigitalCertificates.value = false;
  }
}

function normalizeEnumValue(value) {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }

  return value;
}

function isDigitalCertificateExpired(certificate) {
  if (!certificate?.valid_until) {
    return false;
  }

  return new Date(certificate.valid_until) < new Date();
}

function formatCertificateValidity(value) {
  if (!value) {
    return "validade não informada";
  }

  return new Intl.DateTimeFormat("pt-BR").format(new Date(value));
}

function extractCertificateName(distinguishedName) {
  if (!distinguishedName) {
    return "titular não informado";
  }

  const match = distinguishedName.match(/CN=([^,]+)/);

  return match?.[1]?.trim() || distinguishedName;
}

async function loadAvailableSignatures() {
  try {
    availableSignatures.value = await getSignatures();
  } catch (error) {
    console.error("Erro ao carregar assinaturas:", error);
    availableSignatures.value = [];
  }
}

function handleToggleDynamicSignatures() {
  if (!useDynamicSignatures.value) {
    selectedSignatureIds.value = [];
    currentExportSignatures.value = [];
  }
}

function selectAllSignatures() {
  selectedSignatureIds.value = availableSignatures.value.map((signature) => {
    return signature.id;
  });
}

function clearSelectedSignatures() {
  selectedSignatureIds.value = [];
}

function validateDynamicSignatures() {
  if (!useDynamicSignatures.value) return true;

  if (!availableSignatures.value.length) {
    alert(
      "Nenhuma assinatura cadastrada. Cadastre uma assinatura antes de habilitar assinaturas dinâmicas."
    );
    return false;
  }

  if (!selectedSignatures.value.length) {
    alert("Selecione pelo menos uma assinatura para aplicar no certificado.");
    return false;
  }

  return true;
}

function prepareSignaturesForExport() {
  if (!useDynamicSignatures.value) {
    currentExportSignatures.value = [];
    return;
  }

  currentExportSignatures.value = selectedSignatures.value.map((signature) => {
    return JSON.parse(JSON.stringify(signature));
  });
}

function startProcessSession(mode, total) {
  processState.value = {
    active: true,
    mode,
    status: "running",

    currentStep: "register",
    currentLabel: "Registrando certificado",
    currentName: "",
    currentIndex: 1,
    total,

    processed: 0,
    signed: 0,
    zipped: 0,
    failed: 0,
    published: 0,
    publicationFailed: 0,

    startedAt: new Date(),
    finishedAt: null,

    logs: [],
    errors: [],
    summary: null,
  };

  addProcessLog("info", "Processo iniciado.");
}

function registerPublicationError(error, collaborator = null) {
  const message =
    error?.message ||
    "O certificado foi assinado, mas não pôde ser publicado externamente.";

  processState.value.publicationFailed += 1;

  processState.value.errors.push({
    type: "publication",
    collaborator: collaborator?.name || null,
    document: collaborator?.cpf || collaborator?.document || null,
    code:
      error?.code ||
      error?.publication?.last_error_code ||
      "CERTIFICATE_PUBLICATION_FAILED",
    message,
    occurredAt: new Date().toISOString(),
  });

  addProcessLog(
    "warning",
    `${
      collaborator?.name ? `${collaborator.name}: ` : ""
    }certificado assinado, mas a publicação externa falhou — ${message}`
  );
}

async function publishSignedCertificate(certificate, collaborator = null) {
  try {
    const publishedCertificate = await publishCertificate(certificate.uuid);

    const publication = publishedCertificate?.publication || null;

    console.log(publication);

    if (publication?.status !== "published") {
      const error = new Error(
        publication?.last_error_message ||
          "O servidor não confirmou a publicação do certificado."
      );

      error.code = publication?.last_error_code || "PUBLICATION_NOT_CONFIRMED";

      error.publication = publication;

      throw error;
    }

    processState.value.published += 1;

    addProcessLog(
      "success",
      `${
        collaborator?.name ? `${collaborator.name}: ` : ""
      }publicação externa concluída.`
    );

    return {
      success: true,
      certificate: publishedCertificate,
      publication,
      error: null,
    };
  } catch (error) {
    console.error("Falha na publicação externa do certificado:", error);

    registerPublicationError(error, collaborator);

    return {
      success: false,
      certificate,
      publication: error?.publication || null,
      error,
    };
  }
}

function setProcessStep(stepId, collaborator = null) {
  const step = activeProcessSteps.value.find((item) => item.id === stepId);

  processState.value.currentStep = stepId;
  processState.value.currentLabel = step?.label || "Processando";
  processState.value.currentName = collaborator?.name || "";

  addProcessLog(
    "info",
    `${processState.value.currentLabel}${
      collaborator?.name ? ` • ${collaborator.name}` : ""
    }`
  );
}

function addProcessLog(type, message) {
  processState.value.logs.push({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type,
    message,
    time: new Date().toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  });

  if (processState.value.logs.length > 500) {
    processState.value.logs.shift();
  }
}

function registerProcessError(error, collaborator = null) {
  const message = error?.message || "Falha inesperada durante o processamento.";

  processState.value.errors.push({
    collaborator: collaborator?.name || null,
    document: collaborator?.cpf || collaborator?.document || null,
    message,
    occurredAt: new Date().toISOString(),
  });

  processState.value.failed += 1;

  addProcessLog(
    "error",
    `${collaborator?.name ? `${collaborator.name}: ` : ""}${message}`
  );
}

function finishProcess() {
  const finishedAt = new Date();

  processState.value.active = false;
  processState.value.finishedAt = finishedAt;
  processState.value.status =
    processState.value.failed > 0 ? "warning" : "success";

  processState.value.summary = {
    total: processState.value.total,
    processed: processState.value.processed,
    signed: processState.value.signed,
    published: processState.value.published,
    publicationFailed: processState.value.publicationFailed,
    zipped: processState.value.zipped,
    failed: processState.value.failed,
    finishedAt: finishedAt.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };

  const hasWarnings =
    processState.value.failed > 0 || processState.value.publicationFailed > 0;

  processState.value.status = hasWarnings ? "warning" : "success";

  addProcessLog(
    hasWarnings ? "warning" : "success",
    processState.value.publicationFailed > 0
      ? "Processo finalizado com pendências de publicação externa."
      : "Processo finalizado."
  );
}

function failProcess(error) {
  const finishedAt = new Date();

  processState.value.active = false;
  processState.value.finishedAt = finishedAt;
  processState.value.status = "failed";

  processState.value.summary = {
    total: processState.value.total,
    processed: processState.value.processed,
    signed: processState.value.signed,
    published: processState.value.published,
    publicationFailed: processState.value.publicationFailed,
    zipped: processState.value.zipped,
    failed: processState.value.failed || 1,
    finishedAt: finishedAt.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };

  addProcessLog("error", error?.message || "Processo interrompido.");
}

function clearProcessSummary() {
  processState.value.summary = null;
  processState.value.status = "idle";
  processState.value.logs = [];
  processState.value.errors = [];
}

function getStepStatus(stepId) {
  if (!processState.value.active && processState.value.summary) {
    return "done";
  }

  const stepIndex = activeProcessSteps.value.findIndex((step) => {
    return step.id === stepId;
  });

  if (stepIndex < currentStepIndex.value) {
    return "done";
  }

  if (stepIndex === currentStepIndex.value) {
    return "active";
  }

  return "pending";
}

function generate() {
  if (isExporting.value) return;
  if (!store.training) return alert("É necessário informar um treinamento");
  if (!store.collaborator.cpf) return alert("É necessário informar o CPF");
  if (!store.collaborator.name) return alert("É necessário informar o NOME");
  if (!validateSelectedDigitalCertificate()) return;
  if (!validateDynamicSignatures()) return;

  prepareSignaturesForExport();

  exportSinglePdf();
}

function generateBatch() {
  if (isExporting.value) return;
  if (!store.training) return alert("É necessário informar um treinamento");

  if (!store.collaborators?.length) {
    return alert("Faça o upload de um arquivo Excel primeiro");
  }

  if (!validateSelectedDigitalCertificate()) return;

  if (!validateDynamicSignatures()) return;

  prepareSignaturesForExport();

  exportBatchZip();
}

function handleFileUpload(e) {
  store.collaborators = [];

  const file = e.target.files[0];
  if (!file) return;

  enableBatchButton.value = false;
  selectedFile.value = file?.name;
  const reader = new FileReader();

  reader.onload = (evt) => {
    const data = new Uint8Array(evt.target.result);
    const wb = XLSX.read(data, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];

    const json = XLSX.utils.sheet_to_json(ws, {
      header: ["name", "cpf"],
      range: 1,
      defval: "",
    });
    store.collaborators = json.filter((r) => r.name || r.cpf);
  };
  reader.readAsArrayBuffer(file);
}

async function exportSinglePdf() {
  isExporting.value = true;
  exportTotal.value = 1;
  exportProgress.value = 0;

  startProcessSession("single", 1);

  const collaborator = { ...store.collaborator };

  try {
    processState.value.currentIndex = 1;

    setProcessStep("register", collaborator);

    const certificate = await registerCertificateForCollaborator(collaborator);

    setProcessStep("render", collaborator);

    const blob = await exportCollaboratorPdf(collaborator, certificate);

    setProcessStep("sign", collaborator);

    const uploadResponse = await uploadCertificatePdf(certificate.uuid, blob);

    const signedCertificate =
      uploadResponse?.certificate || uploadResponse || certificate;

    processState.value.signed += 1;

    setProcessStep("publish", collaborator);

    await publishSignedCertificate(
      signedCertificate || certificate,
      collaborator
    );

    setProcessStep("download", collaborator);

    const processedPdf = await downloadCertificatePdf(certificate.uuid);

    saveAs(processedPdf.blob, processedPdf.filename);

    processState.value.processed += 1;
    exportProgress.value = 1;

    setProcessStep("finish", collaborator);
    finishProcess();
  } catch (error) {
    console.error("Erro ao gerar, armazenar e baixar PDF individual:", error);

    registerProcessError(error, collaborator);
    failProcess(error);

    alert(
      error.message ||
        "Não foi possível gerar, processar e baixar o PDF deste certificado."
    );
  } finally {
    resetExportState();
  }
}
async function exportBatchZip() {
  isExporting.value = true;
  exportTotal.value = store.collaborators.length;
  exportProgress.value = 0;

  startProcessSession("batch", store.collaborators.length);

  const zip = new JSZip();
  const usedNames = new Map();

  try {
    for (let index = 0; index < store.collaborators.length; index++) {
      const collaborator = store.collaborators[index];

      processState.value.currentIndex = index + 1;

      try {
        setProcessStep("register", collaborator);

        const certificate = await registerCertificateForCollaborator(
          collaborator
        );

        setProcessStep("render", collaborator);

        const blob = await exportCollaboratorPdf(collaborator, certificate);

        setProcessStep("sign", collaborator);

        const uploadResponse = await uploadCertificatePdf(
          certificate.uuid,
          blob
        );

        const signedCertificate =
          uploadResponse?.certificate || uploadResponse || certificate;

        processState.value.signed += 1;

        setProcessStep("publish", collaborator);

        await publishSignedCertificate(signedCertificate, collaborator);

        setProcessStep("download", collaborator);

        const processedPdf = await downloadCertificatePdf(certificate.uuid);

        setProcessStep("zip", collaborator);

        zip.file(
          processedPdf.filename || buildPdfFileName(collaborator, usedNames),
          processedPdf.blob
        );

        processState.value.zipped += 1;
        processState.value.processed += 1;

        exportProgress.value =
          processState.value.processed + processState.value.failed;

        addProcessLog(
          "success",
          `${collaborator?.name || "Certificado"} concluído.`
        );
      } catch (itemError) {
        console.error("Erro ao processar item do lote:", itemError);

        registerProcessError(itemError, collaborator);

        exportProgress.value =
          processState.value.processed + processState.value.failed;
      }
    }

    if (processState.value.zipped <= 0) {
      throw new Error("Nenhum certificado foi gerado com sucesso no lote.");
    }

    processState.value.currentIndex = processState.value.total;

    setProcessStep("package");
    const zipBlob = await zip.generateAsync({ type: "blob" });

    saveAs(zipBlob, buildZipFileName());

    setProcessStep("finish");
    finishProcess();
  } catch (error) {
    console.error("Erro ao gerar, processar e compactar certificados:", error);

    failProcess(error);

    alert(
      error.message ||
        "Não foi possível gerar, processar e baixar o lote de certificados."
    );
  } finally {
    resetExportState();
  }
}

async function exportCollaboratorPdf(collaborator, certificate) {
  currentExportCollaborator.value = { ...collaborator };
  currentExportCertificate.value = certificate ? { ...certificate } : null;
  exportKey.value += 1;

  await waitForReceiptReady();

  return html2pdf()
    .set(getPdfOptions())
    .from(pdfContainer.value)
    .toPdf()
    .get("pdf")
    .then((pdf) => pdf.output("blob"));
}

async function waitForReceiptReady() {
  await nextTick();
  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const root = pdfContainer.value;
  if (!root) return;

  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map((img) => {
      if (img.complete) return Promise.resolve();

      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    })
  );

  await new Promise((resolve) => setTimeout(resolve, 120));
}

function getPdfOptions() {
  return {
    margin: 0,
    filename: "certificado.pdf",
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "landscape",
    },
    pagebreak: {
      mode: ["css"],
    },
  };
}

function buildPdfFileName(collaborator, usedNames = null) {
  let baseName = sanitizeFileName(collaborator?.name || "certificado");

  if (usedNames) {
    const currentCount = (usedNames.get(baseName) || 0) + 1;
    usedNames.set(baseName, currentCount);

    if (currentCount > 1) {
      baseName = `${baseName}-${currentCount}`;
    }
  }

  return `${baseName}.pdf`;
}

function buildZipFileName() {
  const trainingName =
    sanitizeFileName(
      store.training?.name || store.training?.value || "certificados"
    ) || "certificados";

  return `certificados-${trainingName}.zip`;
}

function sanitizeFileName(value) {
  return String(value || "certificado")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[<>:\"/\\|?*\x00-\x1F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\./g, "")
    .replace(/ /g, "-")
    .slice(0, 120);
}

function resetExportState() {
  currentExportCollaborator.value = null;
  currentExportCertificate.value = null;
  currentExportSignatures.value = [];
  isExporting.value = false;
  exportProgress.value = 0;
  exportTotal.value = 0;
}

function buildCertificatePayload(collaborator) {
  return {
    type: "training_certificate",

    student_name: toRequiredString(collaborator?.name),
    student_document: toNullableString(collaborator?.cpf),

    training_name:
      store.training?.name || store.training?.value || "Treinamento",
    training_value: store.training?.value || null,
    training_payload: {
      description: store.descricao,
      selected_training: store.training,
    },

    company_miniature: store.companies?.m_company || null,
    aproveitamento: store.aproveit ? String(store.aproveit) : null,
    issued_location: store.dateLocale || null,
    issued_at: null,

    metadata: {
      source: "certificador_vue",
      generation_mode: store.collaborators?.length ? "batch" : "single",
      selected_signing_mode: selectedSigningMode.value,
      selected_digital_certificate_id:
        selectedSigningMode.value === "platform"
          ? null
          : selectedDigitalCertificateId.value,
    },

    signed_with_mode: selectedSigningMode.value,
    signed_with_certificate_id:
      selectedSigningMode.value === "platform"
        ? null
        : selectedDigitalCertificateId.value,

    signers: buildSignersPayload(),
  };
}

function buildSignersPayload() {
  if (!useDynamicSignatures.value) return [];

  return selectedSignatures.value.map((signature) => {
    return {
      name: signature.signer?.name || "Assinante",
      role: signature.signer?.role || null,
      registry: signature.signer?.registry || null,
      email: signature.signer?.email || null,
      document: signature.signer?.document || null,

      signature_type: "saved_visual",
      signature_source: "frontend_indexeddb",
      auth_method: "saved_signature_selected_by_user",

      evidence_payload: {
        signature_id: signature.id || null,
        signature_created_at: signature.createdAt || null,
        signature_updated_at: signature.updatedAt || null,
        note: "Assinatura visual previamente cadastrada no navegador e selecionada pelo usuário emissor.",
      },
    };
  });
}

function toNullableString(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const stringValue = String(value).trim();

  return stringValue === "" ? null : stringValue;
}

function toRequiredString(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
}

async function registerCertificateForCollaborator(collaborator) {
  const payload = buildCertificatePayload(collaborator);
  return createCertificate(payload);
}

onMounted(() => {
  loadAvailableSignatures();
  loadDigitalCertificates();
});
</script>

<style scoped>
.dynamic-signatures-panel {
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 22px;
  padding: 1rem;
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.92),
      rgba(248, 250, 252, 0.76)
    ),
    radial-gradient(
      circle at top right,
      rgba(255, 122, 0, 0.08),
      transparent 18rem
    );
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
}

.dynamic-signatures-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.dynamic-signatures-header span {
  display: block;
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dynamic-signatures-header strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
}

.dynamic-signatures-header p {
  max-width: 680px;
  margin: 0.45rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.6;
}

.signature-switch {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  cursor: pointer;
}

.signature-switch input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.signature-switch span {
  position: relative;
  width: 54px;
  height: 30px;
  border-radius: 999px;
  background: #cbd5e1;
  box-shadow: inset 0 2px 6px rgba(15, 23, 42, 0.18);
  transition: 0.2s ease;
}

.signature-switch span::after {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.22);
  transition: 0.2s ease;
}

.signature-switch input:checked + span {
  background: linear-gradient(135deg, #ffb020, #ff7a00);
}

.signature-switch input:checked + span::after {
  transform: translateX(24px);
}

.dynamic-signatures-body {
  margin-top: 1rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.signature-empty-warning {
  border: 1px dashed rgba(249, 115, 22, 0.45);
  border-radius: 16px;
  padding: 0.9rem;
  color: #92400e;
  background: #fff7ed;
  font-size: 0.78rem;
  line-height: 1.6;
}

.signature-selection-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
}

.signature-selection-actions button {
  border: 1px solid #d6deea;
  border-radius: 12px;
  padding: 0.55rem 0.75rem;
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  cursor: pointer;
  background: #ffffff;
  transition: 0.2s ease;
}

.signature-selection-actions button:hover {
  color: #111827;
  border-color: rgba(255, 122, 0, 0.45);
  background: #fffaf3;
}

.signature-selection-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.signature-selection-card {
  display: grid;
  grid-template-columns: auto 120px minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  min-height: 90px;
  border: 1px solid #d6deea;
  border-radius: 18px;
  padding: 0.75rem;
  cursor: pointer;
  background: #ffffff;
  transition: 0.2s ease;
}

.signature-selection-card:hover {
  border-color: rgba(255, 122, 0, 0.38);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.06);
}

.signature-selection-card.selected {
  border-color: rgba(255, 122, 0, 0.8);
  background: linear-gradient(135deg, #ffffff, #fff7ed),
    radial-gradient(
      circle at top right,
      rgba(255, 122, 0, 0.12),
      transparent 12rem
    );
  box-shadow: 0 16px 34px rgba(255, 122, 0, 0.1);
}

.signature-selection-card input {
  width: 17px;
  height: 17px;
  accent-color: #ff7a00;
}

.signature-selection-preview {
  display: grid;
  place-items: center;
  height: 64px;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: linear-gradient(
      45deg,
      rgba(15, 23, 42, 0.04) 25%,
      transparent 25%
    ),
    linear-gradient(-45deg, rgba(15, 23, 42, 0.04) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(15, 23, 42, 0.04) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(15, 23, 42, 0.04) 75%);
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
  background-size: 16px 16px;
  background-color: #ffffff;
}

.signature-selection-preview img {
  max-width: 100px;
  max-height: 48px;
  object-fit: contain;
}

.signature-selection-info {
  min-width: 0;
}

.signature-selection-info strong {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.78rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signature-selection-info span {
  display: block;
  margin-top: 0.25rem;
  color: #334155;
  font-size: 0.72rem;
}

.signature-selection-info small {
  display: block;
  margin-top: 0.28rem;
  color: #64748b;
  font-size: 0.64rem;
  line-height: 1.4;
}

.generation-progress-panel {
  grid-column: 1 / -1;
  margin-top: 1.2rem;
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 24px;
  padding: 1rem;
  background: radial-gradient(
      circle at top right,
      rgba(255, 148, 23, 0.12),
      transparent 20rem
    ),
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.96),
      rgba(248, 250, 252, 0.84)
    );
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.07);
}

.generation-progress-panel.success {
  border-color: rgba(34, 197, 94, 0.32);
}

.generation-progress-panel.warning {
  border-color: rgba(245, 158, 11, 0.42);
}

.generation-progress-panel.failed {
  border-color: rgba(239, 68, 68, 0.42);
}

.generation-progress-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.generation-progress-header span {
  display: block;
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.generation-progress-header strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1rem;
}

.generation-progress-header p {
  margin: 0.4rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.5;
}

.generation-progress-close {
  align-self: flex-start;
  border: 1px solid #d6deea;
  border-radius: 13px;
  padding: 0.52rem 0.8rem;
  color: #334155;
  background: #ffffff;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  cursor: pointer;
}

.generation-progress-bar {
  overflow: hidden;
  height: 10px;
  margin-top: 1rem;
  border-radius: 999px;
  background: #e2e8f0;
}

.generation-progress-bar div {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffb020, #ff7300);
  box-shadow: 0 0 18px rgba(255, 115, 0, 0.35);
  transition: width 0.25s ease;
}

.generation-progress-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.7rem;
  margin-top: 1rem;
}

.generation-progress-stats article {
  padding: 0.82rem;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
}

.generation-progress-stats span {
  display: block;
  color: #64748b;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.generation-progress-stats strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.1rem;
}

.generation-progress-stats article.danger strong {
  color: #dc2626;
}

.generation-step-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
  margin-top: 1rem;
}

.generation-step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 40px;
  padding: 0.55rem 0.65rem;
  border-radius: 15px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.generation-step-marker {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: #cbd5e1;
}

.generation-step small {
  font-size: 0.66rem;
  line-height: 1.25;
  font-family: "XoloniumBold", sans-serif;
}

.generation-step.done {
  color: #166534;
  background: #f0fdf4;
  border-color: rgba(34, 197, 94, 0.28);
}

.generation-step.done .generation-step-marker {
  background: #22c55e;
}

.generation-step.active {
  color: #92400e;
  background: #fff7ed;
  border-color: rgba(249, 115, 22, 0.36);
}

.generation-step.active .generation-step-marker {
  background: #ff7a00;
  box-shadow: 0 0 0 5px rgba(255, 122, 0, 0.13);
}

.generation-log-list {
  display: grid;
  gap: 0.42rem;
  margin-top: 1rem;
  max-height: 260px;
  overflow-y: auto;
}

.generation-log-item {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 0.6rem;
  align-items: start;
  padding: 0.55rem 0.65rem;
  border-radius: 13px;
  background: rgba(248, 250, 252, 0.88);
  border: 1px solid #e2e8f0;
}

.generation-log-item span {
  color: #94a3b8;
  font-size: 0.62rem;
}

.generation-log-item p {
  margin: 0;
  color: #334155;
  font-size: 0.68rem;
  line-height: 1.45;
}

.generation-log-item.success {
  background: #f0fdf4;
  border-color: rgba(34, 197, 94, 0.22);
}

.generation-log-item.warning {
  background: #fffbeb;
  border-color: rgba(245, 158, 11, 0.28);
}

.generation-log-item.error {
  background: #fef2f2;
  border-color: rgba(239, 68, 68, 0.26);
}

.generation-summary-card {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 18px;
  color: #ffffff;
}

.generation-summary-card.all-success {
  background: radial-gradient(
      circle at top right,
      rgba(34, 197, 94, 0.22),
      transparent 18rem
    ),
    linear-gradient(135deg, #052e16, #14532d);
  border: 1px solid rgba(34, 197, 94, 0.35);
  box-shadow: 0 18px 38px rgba(22, 101, 52, 0.16);
}

.generation-summary-card.has-failures {
  background: radial-gradient(
      circle at top right,
      rgba(239, 68, 68, 0.24),
      transparent 18rem
    ),
    linear-gradient(135deg, #450a0a, #7f1d1d);
  border: 1px solid rgba(239, 68, 68, 0.35);
  box-shadow: 0 18px 38px rgba(127, 29, 29, 0.16);
}

.generation-summary-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #052e16;
  background: #bbf7d0;
  font-family: "XoloniumBold", sans-serif;
}

.generation-summary-card.has-failures .generation-summary-icon {
  color: #7f1d1d;
  background: #fecaca;
}

.generation-summary-card strong {
  display: block;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.92rem;
}

.generation-summary-card p {
  margin: 0.45rem 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.76rem;
  line-height: 1.6;
}

.generation-summary-warning {
  color: #fecaca !important;
}

.generation-log-item.error p {
  color: #991b1b;
}

.generation-log-item.error span {
  color: #dc2626;
}

.generation-log-item.success p {
  color: #166534;
}

.generation-log-item.success span {
  color: #16a34a;
}

.cert-generation-form {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  color: #111827;
  text-align: left;
}

.cert-form-intro {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 1rem;
  margin-bottom: 1.2rem;
  padding: 1.05rem;
  border-radius: 24px;
  background: radial-gradient(
      circle at top right,
      rgba(255, 148, 23, 0.12),
      transparent 18rem
    ),
    rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(214, 222, 234, 0.95);
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.cert-form-intro span,
.cert-field label,
.dynamic-signatures-header span {
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.cert-form-intro strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.05rem;
}

.cert-form-intro p {
  max-width: 620px;
  margin: 0.4rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.65;
}

.cert-form-mode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

.cert-form-mode-grid article {
  padding: 0.75rem;
  border-radius: 17px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.cert-form-mode-grid article.active {
  background: #fff7ed;
  border-color: rgba(249, 115, 22, 0.32);
  box-shadow: 0 12px 26px rgba(249, 115, 22, 0.08);
}

.cert-form-mode-grid small {
  display: block;
  color: #64748b;
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.cert-form-mode-grid strong {
  margin-top: 0.25rem;
  color: #111827;
  font-size: 0.78rem;
  line-height: 1.25;
}

.cert-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 1rem;
}

.cert-field,
.cert-field-full {
  min-width: 0;
}

.cert-field-full {
  grid-column: 1 / -1;
}

.cert-field-wide {
  min-width: 0;
}

.cert-field label {
  display: block;
  margin-bottom: 0.45rem;
}

.cert-input {
  width: 100%;
  height: 54px;
  min-height: 54px;
  padding: 0.72rem 0.85rem;
  border: 1px solid #d6deea;
  border-radius: 15px;
  color: #111827;
  background: rgba(248, 250, 252, 0.92);
  outline: none;
  font-size: 0.86rem;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.cert-input-with-icon {
  padding-left: 3.35rem;
}

.cert-input:focus {
  border-color: rgba(249, 115, 22, 0.65);
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.12),
    0 14px 32px rgba(15, 23, 42, 0.08);
}

.cert-textarea {
  height: auto;
  min-height: 112px;
  resize: vertical;
  line-height: 1.65;
}

.cert-upload-field {
  position: relative;
  width: 100%;
}

.cert-excel-upload {
  position: absolute;
  top: 1px;
  bottom: 1px;
  left: 1px;
  width: 48px;
  min-height: 52px;
  z-index: 2;
  display: grid;
  place-items: center;
  cursor: pointer;
  border-radius: 14px 0 0 14px;
  color: #64748b;
  background: #e2e8f0;
  border-right: 1px solid #d6deea;
  transition: 0.18s ease;
}

.cert-excel-upload:hover {
  color: #f97316;
  background: #fff7ed;
}

.cert-excel-upload.uploaded {
  color: #16a34a;
  background: #f0fdf4;
  border-right-color: rgba(34, 197, 94, 0.32);
}

.cert-input-with-icon {
  padding-left: 3.65rem;
  padding-top: 0.65rem;
}

.cert-action-bar {
  display: flex;
  justify-content: center;
  gap: 0.85rem;
  padding: 1.2rem 0 0.4rem;
  font-family: "XoloniumBold", sans-serif;
}

.cert-action-button {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 0 1.4rem;
  border: 0;
  border-radius: 16px;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.9rem;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
}

.cert-action-button.primary {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 16px 34px rgba(22, 163, 74, 0.22);
}

.cert-action-button.secondary {
  background: linear-gradient(135deg, #ffb020, #ff7300);
  box-shadow: 0 16px 34px rgba(255, 115, 0, 0.22);
}

.cert-action-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 20px 42px rgba(15, 23, 42, 0.18);
}

.cert-action-button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
  transform: none;
  box-shadow: none;
}

.cert-select-wrap {
  width: 100%;
  height: 54px;
  min-height: 54px;
  display: flex;
  align-items: center;
  overflow: hidden;
  border: 1px solid #d6deea;
  border-radius: 15px;
  background: rgba(248, 250, 252, 0.92);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.cert-select-wrap:focus-within {
  border-color: rgba(249, 115, 22, 0.65);
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.12),
    0 14px 32px rgba(15, 23, 42, 0.08);
}

.cert-select-wrap :deep(select),
.cert-select-wrap :deep(button),
.cert-select-wrap :deep(.simple-select-trigger),
.cert-select-wrap :deep(.select-trigger) {
  width: 100%;
  height: 52px !important;
  /* min-height: 52px !important; */
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.cert-select-wrap :deep(.simple-select),
.cert-select-wrap :deep(.select-root),
.cert-select-wrap :deep(.relative) {
  width: 100%;
}

.digital-signature-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 1rem;
  align-items: center;
  margin-top: 0.15rem;
  padding: 1rem;
  border-radius: 22px;
  border: 1px solid rgba(214, 222, 234, 0.95);
  background: radial-gradient(
      circle at top right,
      rgba(34, 197, 94, 0.12),
      transparent 18rem
    ),
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.96),
      rgba(248, 250, 252, 0.86)
    );
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.digital-signature-main span {
  display: block;
  color: #16a34a;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.digital-signature-main strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
}

.digital-signature-main p {
  max-width: 620px;
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.6;
}

.digital-certificate-selector {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

.digital-certificate-option {
  position: relative;
  display: block;
  padding: 0.78rem;
  border-radius: 17px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: 0.18s ease;
}

.digital-certificate-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.digital-certificate-option.active {
  background: #f0fdf4;
  border-color: rgba(34, 197, 94, 0.38);
  box-shadow: 0 14px 30px rgba(34, 197, 94, 0.1);
}

.digital-certificate-option span {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.72rem;
}

.digital-certificate-option small {
  display: block;
  margin-top: 0.28rem;
  color: #64748b;
  font-size: 0.58rem;
  line-height: 1.35;
}

.digital-certificate-select-row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) 100px;
  gap: 0.65rem;
  align-items: center;
  padding-top: 0.85rem;
  border-top: 1px solid #e2e8f0;
}

.digital-certificate-select-row label {
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.digital-certificate-select-row select {
  width: 100%;
  min-height: 44px;
  border: 1px solid #d6deea;
  border-radius: 14px;
  padding: 0 0.8rem;
  color: #111827;
  background: #ffffff;
  font-family: "Xolonium", sans-serif;
  outline: none;
}

.digital-certificate-select-row button {
  min-height: 44px;
  border: 1px solid #d6deea;
  border-radius: 14px;
  background: #ffffff;
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.64rem;
  cursor: pointer;
}

.digital-certificate-preview {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

.digital-certificate-preview article {
  padding: 0.75rem;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.digital-certificate-preview small {
  display: block;
  color: #64748b;
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.digital-certificate-preview strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.7rem;
  line-height: 1.35;
}

.digital-certificate-warning {
  grid-column: 1 / -1;
  margin: 0;
  padding: 0.75rem;
  border-radius: 15px;
  color: #92400e;
  background: #fff7ed;
  border: 1px dashed rgba(249, 115, 22, 0.38);
  font-size: 0.72rem;
  line-height: 1.55;
}

@media (max-width: 860px) {
  .dynamic-signatures-header {
    flex-direction: column;
  }

  .signature-selection-list {
    grid-template-columns: 1fr;
  }

  .signature-selection-card {
    grid-template-columns: auto 100px minmax(0, 1fr);
  }

  .generation-progress-header {
    flex-direction: column;
  }

  .generation-progress-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .generation-step-list {
    grid-template-columns: 1fr;
  }

  .generation-log-item {
    grid-template-columns: 1fr;
  }

  .cert-form-intro {
    grid-template-columns: 1fr;
    padding: 0.95rem;
  }

  .cert-form-mode-grid {
    grid-template-columns: 1fr;
  }

  .cert-form-grid {
    grid-template-columns: 1fr;
  }

  .cert-field-full {
    grid-column: auto;
  }

  .cert-action-bar {
    flex-direction: column;
  }

  .cert-action-button {
    width: 100%;
  }

  .digital-signature-card {
    grid-template-columns: 1fr;
  }

  .digital-signature-status {
    grid-template-columns: 1fr;
  }

  .digital-certificate-selector,
  .digital-certificate-preview,
  .digital-certificate-select-row {
    grid-template-columns: 1fr;
  }
}
</style>
